import React, { useCallback, useEffect, useRef, useState } from 'react';

import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { Empty } from '@buildoutinc/blueprint-react/ui/Empty';
import { Table } from '@buildoutinc/blueprint-react/ui/Table';

import { Alert } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import Pagination from 'components/ui/pagination';
import { get, getRaw, post } from 'helpers/api';
import { pluralize } from 'helpers/formatters/string_formats';

import BulkDeleteModal from './bulk_delete_modal';
import BulkResults from './bulk_results';
import ColumnHeader from './column_header';
import PageLibraryFilters from './filters';
import SelectionToolbar from './selection_toolbar';
import UploadModal from './upload_modal';
import ReplaceFileModal from './replace_file_modal';
import PageRow from './page_row';
import DocumentPicker, { orientationMismatch, PickerMode } from './page/document_picker';
import type { CandidateDocument } from './page/types';
import { BulkStatus, Orientation, pageKey, type BulkResult, type ColumnConfig, type FilterOption, type FilterOptions,
  type PageRecord, type SortState } from './types';

interface Props {
  apiEndpoint: string;
  detailBasePath: string;
  detailCompanyId: number | null;
}

interface IndexResponse {
  records: PageRecord[];
  filterOptions: FilterOptions;
  meta: {
    currentPage: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
}

const COLUMNS: ColumnConfig[] = [
  { key: 'name', label: 'Name', sortKey: 'name' },
  { key: 'orientation', label: 'Orientation' },
  { key: 'usedIn', label: 'Used In', sortKey: 'documents_count' },
  { key: 'createdBy', label: 'Created By', sortKey: 'created_by_name' },
  { key: 'lastEdited', label: 'Last Edited', sortKey: 'updated_at' },
];

// Zero documents is a valid outcome: the page lands in the library unattached.
const uploadedMessage = (pageName: string, documentCount: number) => {
  if (documentCount === 0) return `Added ${pageName} to your library.`;

  return `Added ${pageName} to ${pluralize(documentCount, 'document')}.`;
};

const replacedMessage = (pageName: string, documentCount: number) => {
  if (documentCount === 0) return `Replaced the file behind ${pageName}.`;

  return `Replaced the file behind ${pageName} in ${pluralize(documentCount, 'document')}.`;
};

const BulkAction = { ...PickerMode, Delete: 'delete' } as const;

type BulkAction = (typeof BulkAction)[keyof typeof BulkAction];

const BULK_PATHS: Record<BulkAction, string> = {
  [BulkAction.Add]: 'documents/bulk',
  [BulkAction.Remove]: 'documents/bulk_remove',
  [BulkAction.Delete]: 'pages/bulk_delete',
};

const keyOf = (page: PageRecord) => pageKey(page.templateType, page.id);

export default function PageLibrary({ apiEndpoint, detailBasePath, detailCompanyId }: Props) {
  const [pages, setPages] = useState<PageRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);

  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [pageTypeFilter, setPageTypeFilter] = useState<string[]>([]);
  const [documentFilter, setDocumentFilter] = useState<string[]>([]);
  const [createdByFilter, setCreatedByFilter] = useState<string[]>([]);
  const [orientationFilter, setOrientationFilter] = useState<string[]>([]);
  const [tagsFilter, setTagsFilter] = useState<string[]>([]);
  const [usageFilter, setUsageFilter] = useState<string[]>([]);
  const [sortState, setSortState] = useState<SortState>({ name: 'name', direction: 'asc' });
  const [uploading, setUploading] = useState(false);
  const [replacing, setReplacing] = useState<PageRecord | null>(null);
  // One success notice at a time, from whichever action landed last.
  const [notice, setNotice] = useState<string | null>(null);

  const [pageTypeOptions, setPageTypeOptions] = useState<FilterOption[]>([]);
  const [documentOptions, setDocumentOptions] = useState<FilterOption[]>([]);
  const [createdByOptions, setCreatedByOptions] = useState<FilterOption[]>([]);
  const [tagsOptions, setTagsOptions] = useState<FilterOption[]>([]);
  const [orientationOptions, setOrientationOptions] = useState<FilterOption[]>([]);

  const [selected, setSelected] = useState<Map<string, PageRecord>>(new Map());
  const [bulkMode, setBulkMode] = useState<PickerMode | null>(null);
  const [candidates, setCandidates] = useState<CandidateDocument[]>([]);
  const [bulkResults, setBulkResults] = useState<{ verb: string; results: BulkResult[] } | null>(null);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [confirmingBulkDelete, setConfirmingBulkDelete] = useState(false);
  // Set when a row's menu opened the flow, so it acts on that page and leaves the checkboxes alone.
  const [rowTarget, setRowTarget] = useState<PageRecord | null>(null);

  const requestSeq = useRef(0);
  // Opening any bulk flow supersedes a candidates request still in flight.
  const bulkOpenSeq = useRef(0);

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearchText(searchText), 300);
    return () => clearTimeout(timeout);
  }, [searchText]);

  const fetchPages = useCallback(async () => {
    const requestId = ++requestSeq.current;
    setLoading(true);
    try {
      const response: IndexResponse = await get(apiEndpoint, {
        params: {
          page: currentPage,
          search: debouncedSearchText || undefined,
          pageType: pageTypeFilter.length > 0 ? pageTypeFilter : undefined,
          documentId: documentFilter.length > 0 ? documentFilter : undefined,
          createdBy: createdByFilter.length > 0 ? createdByFilter : undefined,
          orientation: orientationFilter.length > 0 ? orientationFilter : undefined,
          tags: tagsFilter.length > 0 ? tagsFilter : undefined,
          usage: usageFilter[0],
          sortBy: sortState.name,
          sortDirection: sortState.direction,
        },
      });
      if (requestId !== requestSeq.current) return;

      setError(false);
      setPages(response.records);
      // Checked pages keep the record they were checked with; swap in the fresh
      // one so a later toolbar action sees the page's current documents.
      setSelected((current) => {
        const refreshed = response.records.filter(page => current.has(keyOf(page)));
        if (refreshed.length === 0) return current;

        const next = new Map(current);
        refreshed.forEach(page => next.set(keyOf(page), page));
        return next;
      });
      setTotalCount(response.meta.totalCount);
      setTotalPages(response.meta.totalPages);
      setPageTypeOptions(response.filterOptions.pageTypes);
      setDocumentOptions(response.filterOptions.documents.map(option => (
        { value: String(option.value), label: option.label }
      )));
      setCreatedByOptions(response.filterOptions.createdBy);
      setTagsOptions(response.filterOptions.tags);
      setOrientationOptions(response.filterOptions.orientations);
    } catch {
      if (requestId !== requestSeq.current) return;

      setError(true);
    } finally {
      if (requestId === requestSeq.current) setLoading(false);
    }
  }, [
    apiEndpoint, currentPage, debouncedSearchText, pageTypeFilter, documentFilter,
    createdByFilter, orientationFilter, tagsFilter, usageFilter, sortState,
  ]);

  useEffect(() => {
    fetchPages();
  }, [fetchPages]);

  // A fresh action supersedes the last one's notice.
  const clearMessages = () => {
    setNotice(null);
    setBulkResults(null);
  };

  const goToPage = (page: number) => {
    clearMessages();
    setCurrentPage(page);
  };

  const resetToFirstPage = () => goToPage(1);

  const handleSort = (sortKey: string, direction: 'asc' | 'desc') => {
    setSortState({ name: sortKey, direction });
    resetToFirstPage();
  };

  const makeFilterHandler = (setter: (values: string[]) => void) => (values: string[]) => {
    setter(values);
    resetToFirstPage();
  };

  const handleSearchChange = (value: string) => {
    setSearchText(value);
    resetToFirstPage();
  };

  const handleClearAllFilters = () => {
    setSearchText('');
    setPageTypeFilter([]);
    setDocumentFilter([]);
    setCreatedByFilter([]);
    setOrientationFilter([]);
    setTagsFilter([]);
    setUsageFilter([]);
    resetToFirstPage();
  };

  const setPagesSelected = (targets: PageRecord[], isSelected: boolean) => {
    setSelected((current) => {
      const next = new Map(current);
      targets.forEach((page) => {
        if (isSelected) {
          next.set(keyOf(page), page);
        } else {
          next.delete(keyOf(page));
        }
      });
      return next;
    });
  };

  const handleSelectedChange = (page: PageRecord, isSelected: boolean) => setPagesSelected([page], isSelected);

  const allSelected = pages.length > 0 && pages.every(page => selected.has(keyOf(page)));

  const handleSelectAll = (checked: boolean) => setPagesSelected(pages, checked);

  const clearSelection = () => setSelected(new Map());

  const selectedPages = [...selected.values()];
  const targetsFor = (row: PageRecord | null) => (row ? [row] : selectedPages);
  const bulkTargets = targetsFor(rowTarget);
  const soloTarget = bulkTargets.length === 1 ? bulkTargets[0] : null;
  const companyQuery = detailCompanyId ? `?company_id=${detailCompanyId}` : '';
  const libraryBase = detailBasePath.replace(/\/pages$/, '');
  const candidatesUrl = `${libraryBase}/documents${companyQuery}`;
  const bulkUrl = (action: BulkAction) => `${libraryBase}/${BULK_PATHS[action]}${companyQuery}`;

  const hasActiveFilters = debouncedSearchText !== ''
    || pageTypeFilter.length > 0
    || documentFilter.length > 0
    || createdByFilter.length > 0
    || orientationFilter.length > 0
    || tagsFilter.length > 0
    || usageFilter.length > 0;

  // The server reports per page; a request that never landed is reported the same way.
  const failureResults = (targets: PageRecord[]): BulkResult[] => targets.map(page => ({
    pageType: page.templateType,
    pageId: page.id,
    name: page.name,
    status: BulkStatus.Skipped,
    reason: 'The request failed',
  }));

  const openBulk = async (mode: PickerMode, row: PageRecord | null = null) => {
    clearMessages();
    setRowTarget(row);
    const openId = ++bulkOpenSeq.current;
    try {
      const response: { documents: CandidateDocument[] } = await getRaw(candidatesUrl);
      if (openId !== bulkOpenSeq.current) return;

      setCandidates(response.documents);
      setBulkMode(mode);
    } catch {
      if (openId !== bulkOpenSeq.current) return;

      setBulkResults({ verb: 'updated', results: failureResults(targetsFor(row)) });
    }
  };

  // Remove offers a document when any target page is in it; add hides one
  // only when every target page already is.
  const pickerDocuments = candidates.map((doc) => {
    const holders = bulkTargets.filter(page => page.documents.some(usage => usage.documentTemplateId === doc.id));
    const inDocument = bulkMode === PickerMode.Remove ? holders.length > 0 : holders.length === bulkTargets.length;

    return { ...doc, in_document: inDocument };
  });

  // A document is out for the whole selection if any selected page would not fit it.
  const bulkMisfitPages = (doc: CandidateDocument): string[] => bulkTargets
    .filter(page => orientationMismatch(doc, page.orientation))
    .map(page => page.name);

  // A toolbar action is done with its selection. A row action keeps it, less
  // any page it deleted, so the toolbar never counts a page that is gone.
  const settleSelection = (action: BulkAction, results: BulkResult[]) => {
    if (!rowTarget) {
      clearSelection();
      return;
    }
    if (action !== BulkAction.Delete) return;

    const deleted = new Set(results.filter(r => r.status === BulkStatus.Done).map(r => pageKey(r.pageType, r.pageId)));
    setPagesSelected(bulkTargets.filter(page => deleted.has(keyOf(page))), false);
  };

  // A failed request closes the modal too, so its alert is visible, but keeps
  // the selection for a retry.
  const runBulk = async (action: BulkAction, verb: string, body: { documentTemplateIds?: number[] } = {}) => {
    setBulkSaving(true);
    try {
      const response: { results: BulkResult[] } = await post(bulkUrl(action), {
        pages: bulkTargets.map(page => ({ pageType: page.templateType, pageId: page.id })),
        ...body,
      });
      setBulkResults({ verb, results: response.results });
      settleSelection(action, response.results);
      fetchPages();
    } catch {
      setBulkResults({ verb, results: failureResults(bulkTargets) });
    } finally {
      setBulkSaving(false);
      setBulkMode(null);
      setConfirmingBulkDelete(false);
    }
  };

  const submitBulk = (documentIds: number[]) => {
    if (!bulkMode) return;

    const phrase = bulkMode === PickerMode.Add ? 'added to' : 'removed from';
    runBulk(bulkMode, `${phrase} ${pluralize(documentIds.length, 'document')}`, { documentTemplateIds: documentIds });
  };

  const openBulkDelete = (row: PageRecord | null = null) => {
    clearMessages();
    setRowTarget(row);
    bulkOpenSeq.current += 1;
    setConfirmingBulkDelete(true);
  };

  const submitBulkDelete = () => runBulk(BulkAction.Delete, 'deleted');

  const openUpload = () => {
    clearMessages();
    setUploading(true);
  };

  const openReplace = (page: PageRecord) => {
    clearMessages();
    setReplacing(page);
  };

  const handleReplaced = (documentCount: number) => {
    if (!replacing) return;

    setNotice(replacedMessage(replacing.name, documentCount));
    setReplacing(null);
    fetchPages();
  };

  const uploadButton = (
    <Button icon={faPlus} variant="primary" onClick={openUpload}>
      Upload PDF
    </Button>
  );

  const renderEmptyState = () => {
    if (hasActiveFilters) {
      return (
        <Empty>
          <Empty.Title>No pages match those filters</Empty.Title>
          <Empty.Content>
            <p>Try a different search term or filter, or upload a new page.</p>
            <div className="d-flex justify-content-center gap-2">
              <Button variant="secondary" onClick={handleClearAllFilters}>Clear filters</Button>
              {uploadButton}
            </div>
          </Empty.Content>
        </Empty>
      );
    }

    return (
      <Empty>
        <Empty.Title>Your page library is empty</Empty.Title>
        <Empty.Content>
          <p>Upload a PDF to add your first page.</p>
          {uploadButton}
        </Empty.Content>
      </Empty>
    );
  };

  const renderResults = () => {
    if (error) {
      return (
        <Alert severity="destructive">
          <Alert.Title>Your page library could not be loaded.</Alert.Title>
          <Button variant="secondary" onClick={fetchPages}>Try again</Button>
        </Alert>
      );
    }

    if (pages.length === 0 && totalCount === 0) return renderEmptyState();

    return (
      <>
        <SelectionToolbar
          count={selected.size}
          onAdd={() => openBulk(PickerMode.Add)}
          onClear={clearSelection}
          onDelete={() => openBulkDelete()}
          onRemove={() => openBulk(PickerMode.Remove)}
        />
        <Table className="align-middle table-hover">
          <Table.Header>
            <Table.Row>
              <Table.Head style={{ width: '32px' }}>
                <Checkbox
                  aria-label="Select all pages"
                  checked={allSelected}
                  onCheckedChange={(checked: boolean | 'indeterminate') => handleSelectAll(checked === true)}
                />
              </Table.Head>
              {COLUMNS.map(column => (
                <ColumnHeader
                  columnConfig={column}
                  key={column.key}
                  sortState={sortState}
                  onSort={handleSort}
                />
              ))}
              <Table.Head />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {pages.map(page => (
              <PageRow
                detailBasePath={detailBasePath}
                detailCompanyId={detailCompanyId}
                key={keyOf(page)}
                page={page}
                selected={selected.has(keyOf(page))}
                onAddToDocuments={row => openBulk(PickerMode.Add, row)}
                onDelete={openBulkDelete}
                onRemoveFromDocuments={row => openBulk(PickerMode.Remove, row)}
                onReplaceFile={openReplace}
                onSelectedChange={handleSelectedChange}
              />
            ))}
          </Table.Body>
        </Table>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onChange={goToPage}
        />
      </>
    );
  };

  if (loading) {
    return (
      <div>
        <h4 className="mb-4">Page Library</h4>
        <div className="mt-2 text-center">
          <div className="spinner-border spinner-xl" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h4 className="mb-4">Page Library</h4>

      <PageLibraryFilters
        actions={pages.length > 0 && uploadButton}
        createdByFilter={createdByFilter}
        createdByOptions={createdByOptions}
        documentFilter={documentFilter}
        documentOptions={documentOptions}
        orientationFilter={orientationFilter}
        orientationOptions={orientationOptions}
        pageTypeFilter={pageTypeFilter}
        pageTypeOptions={pageTypeOptions}
        searchText={searchText}
        tagsFilter={tagsFilter}
        tagsOptions={tagsOptions}
        usageFilter={usageFilter}
        onCreatedByChange={makeFilterHandler(setCreatedByFilter)}
        onDocumentChange={makeFilterHandler(setDocumentFilter)}
        onOrientationChange={makeFilterHandler(setOrientationFilter)}
        onPageTypeChange={makeFilterHandler(setPageTypeFilter)}
        onSearchChange={handleSearchChange}
        onSearchClear={() => handleSearchChange('')}
        onTagsChange={makeFilterHandler(setTagsFilter)}
        onUsageChange={makeFilterHandler(setUsageFilter)}
      />

      {notice && (
        <Alert className="mb-3" severity="success">
          <Alert.Title>{notice}</Alert.Title>
        </Alert>
      )}

      {bulkResults && <BulkResults results={bulkResults.results} verb={bulkResults.verb} />}

      {renderResults()}

      <DocumentPicker
        documents={pickerDocuments}
        key={bulkMode ?? 'bulk-closed'}
        // One page gets the picker's own orientation reason; a selection names its misfits.
        misfitPages={soloTarget ? undefined : bulkMisfitPages}
        mode={bulkMode ?? PickerMode.Add}
        pageNames={bulkTargets.map(page => page.name)}
        pageOrientation={soloTarget?.orientation ?? Orientation.Unknown}
        saving={bulkSaving}
        show={bulkMode !== null}
        submitLabel={bulkMode === PickerMode.Remove ? 'Remove from documents' : 'Add to documents'}
        onClose={() => setBulkMode(null)}
        onSubmit={submitBulk}
      />

      <BulkDeleteModal
        pages={bulkTargets}
        saving={bulkSaving}
        show={confirmingBulkDelete}
        onClose={() => setConfirmingBulkDelete(false)}
        onConfirm={submitBulkDelete}
      />

      <UploadModal
        basePath={detailBasePath}
        companyId={detailCompanyId}
        key={uploading ? 'open' : 'closed'}
        show={uploading}
        tagSuggestions={tagsOptions.map(option => option.value)}
        onClose={(libraryChanged) => {
          setUploading(false);
          if (libraryChanged) fetchPages();
        }}
        onUploaded={(pageName, documentCount) => {
          setUploading(false);
          setNotice(uploadedMessage(pageName, documentCount));
          // Show the list from the top, as every filter change does. setCurrentPage
          // rather than goToPage so the notice above survives; the page effect refetches.
          if (currentPage === 1) {
            fetchPages();
          } else {
            setCurrentPage(1);
          }
        }}
      />

      {replacing && (
        <ReplaceFileModal
          basePath={detailBasePath}
          companyId={detailCompanyId}
          documentCount={replacing.documentsCount}
          documents={replacing.documents.map(usage => (
            { id: usage.documentTemplateId, name: usage.documentTemplateName }
          ))}
          page={{
            id: replacing.id,
            name: replacing.name,
            orientation: replacing.orientation,
            fileName: replacing.fileName,
          }}
          show
          onClose={() => setReplacing(null)}
          onReplaced={handleReplaced}
        />
      )}
    </div>
  );
}
